import { computed } from 'vue';
import { IUnsavedCustomProductData, IUnsavedCustomProductDataForIdDeletion } from '@/models/custom-products.interface';
import { IPresentableEntity } from '@/models/available-entities.interface';
import {
	CampaignType, Currency, DeliveryMode, PartnershipType, Region, MinimalOrganisationDto, CampaignListingEntityDto, CreateCampaignDto,
	CustomProductDto, CampaignEditorGetResultDto, UpdatedCampaignDataDto, CampaignMandatoryFieldsValidatorInputShape, SearchUserDto
} from '@studyportals/campaign-management-api-interface';
import store from '@/store';

class SavingValidationPayloadPresenter {

	private campaignId = computed((): number => {
		const router = store.getters.router();
		return router.props.id;
	});

	private name = computed((): string => {
		return store.getters.name();
	});

	private startDate = computed((): Date | null => {
		return store.getters.startDate();
	});

	private endDate = computed((): Date | null => {
		return store.getters.endDate();
	});

	private partnershipType = computed((): PartnershipType | null => {
		return store.getters.partnershipType();
	});

	private deliveryMode = computed((): DeliveryMode => {
		return store.getters.deliveryMode();
	});

	private campaignType = computed((): CampaignType | null => {
		return store.getters.campaignType();
	});

	private currency = computed((): Currency | null => {
		return store.getters.currency();
	});

	private budgetListing = computed((): number | null => {
		return store.getters.budgetListing();
	});

	private clientServicesPerson = computed((): number | null => {
		return store.getters.clientServicesPerson();
	});

	private accountManager = computed((): number | null => {
		return store.getters.accountManager();
	});

	private crmId = computed((): number | null => {
		return store.getters.crmId();
	});

	private region = computed((): Region => {
		return store.getters.region();
	});

	private campaignLiftExcluded = computed((): boolean => {
		return store.getters.campaignLiftExcluded();
	});

	private linkedOrganisations = computed((): MinimalOrganisationDto[] => {
		return store.getters.linkedOrganisations();
	});

	private budgetCustom = computed((): number | null => {
		return store.getters.budgetCustom();
	});

	private customProducts = computed((): (IUnsavedCustomProductData | CustomProductDto)[] => {
		return store.getters.customProducts();
	});

	private availableEntities = computed((): IPresentableEntity[] => {
		return store.getters.availableEntities();
	});

	private rightsUsers = computed((): SearchUserDto[] => {
		return store.getters.rightsUsers();
	});

	private persistGLParamRefLink = computed((): boolean => {
		return store.getters.persistGLParamRefLink();
	});

	private internalNotes = computed((): string => {
		return store.getters.internalNotes();
	});

	public createValidationPayload(): CampaignMandatoryFieldsValidatorInputShape {
		return {
			name: this.name.value,
			startDate: this.startDate.value,
			endDate: this.endDate.value,
			partnershipType: this.partnershipType.value,
			deliveryMode: this.deliveryMode.value,
			campaignType: this.campaignType.value,
			currency: this.currency.value,
			listingBudget: this.budgetListing.value,
			clientServicesPerson: this.clientServicesPerson.value,
			accountManager: this.accountManager.value,
			crmId: this.crmId.value,
			region: this.region.value,
			customProductsBudget: this.budgetCustom.value,
			isExcludedFromCampaignLift: this.campaignLiftExcluded.value,
			persistGLParamRefLink: this.persistGLParamRefLink.value
		};
	}

	public createCreationPayload(): CreateCampaignDto | null {
		if (!this.startDate.value ||
			!this.endDate.value ||
			!this.partnershipType.value ||
			!this.deliveryMode.value ||
			!this.campaignType.value ||
			!this.currency.value ||
			!this.clientServicesPerson.value ||
			!this.accountManager.value ||
			!this.crmId.value ||
			typeof this.budgetListing.value !== 'number' ||
			typeof this.budgetCustom.value !== 'number') {
			return null;
		}

		return {
			name: this.name.value,
			startDate: this.startDate.value,
			endDate: this.endDate.value,
			partnershipType: this.partnershipType.value,
			deliveryMode: this.deliveryMode.value,
			campaignType: this.campaignType.value,
			currency: this.currency.value,
			listingBudget: this.budgetListing.value,
			clientServicesPerson: this.clientServicesPerson.value,
			accountManager: this.accountManager.value,
			crmId: this.crmId.value,
			region: this.region.value,
			customProductsBudget: this.budgetCustom.value,
			isExcludedFromCampaignLift: this.campaignLiftExcluded.value,
			persistGLParamRefLink: this.persistGLParamRefLink.value
		};
	}

	public createSavingPayload(): UpdatedCampaignDataDto {
		const validationPayload = this.createValidationPayload();
		const extraPropertiesForSaving = {
			id: this.campaignId.value,
			isExcludedFromCampaignLift: this.campaignLiftExcluded.value,
			linkedOrganisations: [...this.linkedOrganisations.value],
			internalNotes: this.internalNotes.value
		};

		return {
			baseCampaignData: {
				...validationPayload,
				...extraPropertiesForSaving
			} as CampaignEditorGetResultDto,
			customProducts: this.prepareCustomProductsForSaving(),
			campaignListingEntities: this.prepareListingEntitiesForSaving(),
			clientsWithAccess: [...this.rightsUsers.value]
		};
	}

	private prepareCustomProductsForSaving(): CustomProductDto[] {
		return this.customProducts.value.map((product) => {
			const productCopy = {
				...product
			};
			if (typeof productCopy.id !== 'string') {
				return productCopy;
			}
			// String-based ids are only used for new custom products; they're not relevant for saving.
			delete (productCopy as IUnsavedCustomProductDataForIdDeletion).id;
			return productCopy;
		}) as CustomProductDto[];
	}

	private prepareListingEntitiesForSaving(): CampaignListingEntityDto[] {
		return this.availableEntities.value.map((entity) => ({
			attached: entity.attached,
			name: entity.name,
			parent: entity.parent,
			status: entity.status,
			degreeLevel: entity.degreeLevel,
			id: entity.id
		})) as CampaignListingEntityDto[];
	}
}

export default new SavingValidationPayloadPresenter();
